import { useCallback, useMemo } from 'react';
import { DataGridPremium, GridColDef, GridRowClassNameParams } from '@mui/x-data-grid-premium';
import { Box } from '@mui/material';
import {
  CustomColumnMenu,
  CustomSortableColumn,
  NoTableRowsOverlay,
  StackedTextCell,
} from '@retail/components';
import { AssortmentPrices } from '@retail/my-assortment/types';
import { AssortmentItemActions } from './AssortmentItemActions';
import { useAppTFunction } from '@retail/app/i18n';
import {
  DATA_GRID_ID,
  getInitialHiddenDataGridColumnsFromLocalStorage,
  getLLDate,
  saveHiddenDataGridColumnsToLocalStorage,
} from '@retail/utils';
import { getTogglableDataGridColumns } from '@shared/utils';
import { useItemInfoColumns, usePriceListColumns } from '../AssortmentTable';
import { formatPrice } from '@retail/products/utils';
import { useTranslation } from 'react-i18next';
import { Action } from '@retail/auth/types';
import { OrgUnitType } from '@retail/mgpris/config-types';
import {
  dataGridCellNoFocusOutlineClass,
  dataGridRowHoverClass,
  dataGridSelectedRowClass,
} from '@shared/styles';
import clsx from 'clsx';
import { ExternalItemSource, PricePerUnit } from '@retail/products/components';
import { useRowColorGrouping } from '@retail/hooks';
import { useItemPriceColumns, useConditionColumns } from '../AssortmentTable/AssortmentColumns';

const baseColumnProps: Partial<GridColDef> = {
  headerName: '',
  headerAlign: 'left',
  align: 'left',
  width: 140,
};

export type AssortmentItemsTablePermissions = {
  assortment: Action[];
  purchaseAgreements: Action[];
  freight: Action[];
  markup: Action[];
  customerMarkup: Action[];
  priceSuggestions: Action[];
};

interface Props {
  items: AssortmentPrices[];
  loading: boolean;
  permissions: AssortmentItemsTablePermissions;
  orgType: OrgUnitType;
  includeVat: boolean;
  selectedCalculusCardItem: AssortmentPrices | null;
  viewItemProductCard: (supplierItemId: number) => void;
  viewItemCalculusCard?: (supplierItemId: number) => void;
  onAddToAssortment?: (supplierItem: AssortmentPrices) => void;
  externalItemSource: ExternalItemSource;
}

export const AssortmentItemsCalculusViewTable = ({
  items,
  loading,
  selectedCalculusCardItem,
  includeVat,
  onAddToAssortment,
  viewItemProductCard,
  viewItemCalculusCard,
  permissions,
  externalItemSource,
  orgType,
}: Props) => {
  const t = useAppTFunction();
  const { i18n } = useTranslation();

  const {
    itemIdsColumn,
    itemNameColumn,
    productGroupColumn,
    isStockedColumn,
    discountGroupColumn,
    discontinuationColumn,
  } = useItemInfoColumns<AssortmentPrices>({ externalItemSource });
  const {
    purchasePriceColumn,
    futurePurchasePriceColumn,
    costPriceInSalesUnitColumn,
    salesPriceColumn,
    futureSalesPriceColumn,
    salesPriceCoverageColumn,
    priceSourceColumn,
  } = useItemPriceColumns({
    includeVat,
  });

  const {
    customerMarkupFactorColumn,
    MGLSalesPriceColumn,
    MGLSalesCoverageColumn,
    customerSalesPriceColumn,
    customerSalesCoverageColumn,
  } = usePriceListColumns({ includeVat });

  const { purchaseConditionColumn, freightConditionColumn } = useConditionColumns();

  const hasAnyMarkupReadPermission =
    permissions.markup.includes('READ') || permissions.customerMarkup.includes('READ');

  const { getRowColorForStripedTable } = useRowColorGrouping();

  const getRowClassName = useCallback(
    (value: GridRowClassNameParams<AssortmentPrices>) => {
      const rowColor = getRowColorForStripedTable(value);
      return clsx(rowColor, dataGridRowHoverClass, {
        [dataGridSelectedRowClass]:
          selectedCalculusCardItem?.mgSupplierItemId === value.row.mgSupplierItemId,
      });
    },
    [getRowColorForStripedTable, selectedCalculusCardItem?.mgSupplierItemId]
  );

  const columnConfigs: { show: boolean; column: GridColDef<AssortmentPrices> }[] = useMemo(
    () => [
      {
        show: true,
        column: itemIdsColumn,
      },
      {
        show: true,
        column: itemNameColumn,
      },
      {
        show: true,
        column: productGroupColumn,
      },
      {
        show: true,
        column: discountGroupColumn,
      },
      {
        show: permissions.purchaseAgreements.includes('READ'),
        column: purchaseConditionColumn,
      },
      {
        show: permissions.purchaseAgreements.includes('READ'),
        column: purchasePriceColumn,
      },
      { show: permissions.purchaseAgreements.includes('READ'), column: futurePurchasePriceColumn },
      {
        show: permissions.freight.includes('READ'),
        column: freightConditionColumn,
      },
      {
        show:
          permissions.purchaseAgreements.includes('READ') && permissions.freight.includes('READ'),
        column: costPriceInSalesUnitColumn,
      },
      {
        show:
          permissions.purchaseAgreements.includes('READ') &&
          permissions.freight.includes('READ') &&
          permissions.markup.includes('READ'),
        column: salesPriceColumn,
      },
      {
        show:
          permissions.purchaseAgreements.includes('READ') &&
          permissions.freight.includes('READ') &&
          permissions.markup.includes('READ'),
        column: futureSalesPriceColumn,
      },
      {
        show: orgType === OrgUnitType.WHOLESALE_PRICE_LIST,
        column: MGLSalesPriceColumn,
      },
      {
        show: orgType === OrgUnitType.WHOLESALE_PRICE_LIST,
        column: MGLSalesCoverageColumn,
      },
      {
        show: orgType === OrgUnitType.WHOLESALE_PRICE_LIST,
        column: customerMarkupFactorColumn,
      },
      {
        show: orgType === OrgUnitType.WHOLESALE_PRICE_LIST,
        column: customerSalesPriceColumn,
      },
      {
        show: orgType === OrgUnitType.WHOLESALE_PRICE_LIST,
        column: customerSalesCoverageColumn,
      },
      {
        show: hasAnyMarkupReadPermission && orgType !== OrgUnitType.WHOLESALE_PRICE_LIST,
        column: salesPriceCoverageColumn,
      },

      {
        show: hasAnyMarkupReadPermission,
        column: priceSourceColumn,
      },
      {
        show: true,
        column: {
          ...baseColumnProps,
          field: 'basePrice',
          headerName: `${t('products.mgItem.details.basePrice')} ${t(
            'products.vat.excluded'
          ).toLowerCase()}`,
          width: 180,
          renderCell: ({ row }) => {
            const basePriceSuggestion = row.supplierBasePrice;
            const baseUnit = row.basePriceUnit;

            return basePriceSuggestion ? (
              <StackedTextCell
                nullableBottomLine
                topLine={
                  <PricePerUnit
                    unit={baseUnit}
                    price={formatPrice(basePriceSuggestion, i18n.language)}
                  />
                }
                bottomLine={getLLDate(row.supplierBasePriceDate)}
              />
            ) : (
              '-'
            );
          },
        },
      },
      {
        show: true,
        column: {
          ...baseColumnProps,
          field: 'finfoRecPrice',
          headerName: `${t('products.mgItem.supplierItem.finfoRecommendedPrice')} ${t(
            'products.vat.included'
          ).toLowerCase()}`,

          width: 180,
          valueGetter: (_, row) => row.supplierRecommendedPriceIncVat,
          renderCell: ({ row }) => {
            const recPriceSuggestion = row.supplierRecommendedPriceIncVat;
            const baseUnit = row.basePriceUnit;

            return recPriceSuggestion ? (
              <StackedTextCell
                nullableBottomLine
                topLine={
                  <PricePerUnit
                    unit={baseUnit}
                    price={formatPrice(recPriceSuggestion, i18n.language)}
                  />
                }
                bottomLine={getLLDate(row.supplierRecommendedPriceDate)}
              />
            ) : (
              '-'
            );
          },
        },
      },
      { show: permissions.assortment.includes('READ'), column: isStockedColumn },
      {
        show: true,
        column: discontinuationColumn,
      },
      {
        show: true,
        column: {
          ...baseColumnProps,
          field: 'createdByName',
          headerName: t('myAssortment.columns.addedBy'),
          valueGetter: (_, row) => row.priority.addedByUser,
          renderCell: ({ row }) =>
            row.priority.addedByUser
              ? `${row.priority.addedByUser.givenName} ${row.priority.addedByUser.familyName}`
              : '-',
        },
      },
      {
        show: true,
        column: {
          ...baseColumnProps,
          field: 'actions',
          align: 'center',
          width: 100,
          disableColumnMenu: true,
          renderCell: ({ row }) => (
            <AssortmentItemActions
              addToAssortment={onAddToAssortment ? () => onAddToAssortment(row) : undefined}
              viewProductCard={() => viewItemProductCard(row.mgSupplierItemId)}
              viewCalculusCard={
                viewItemCalculusCard ? () => viewItemCalculusCard(row.mgSupplierItemId) : undefined
              }
            />
          ),
        },
      },
    ],
    [
      itemIdsColumn,
      itemNameColumn,
      productGroupColumn,
      discountGroupColumn,
      permissions.purchaseAgreements,
      permissions.freight,
      permissions.markup,
      permissions.assortment,
      purchaseConditionColumn,
      purchasePriceColumn,
      futurePurchasePriceColumn,
      orgType,
      t,
      freightConditionColumn,
      costPriceInSalesUnitColumn,
      salesPriceColumn,
      futureSalesPriceColumn,
      MGLSalesPriceColumn,
      MGLSalesCoverageColumn,
      customerMarkupFactorColumn,
      customerSalesPriceColumn,
      customerSalesCoverageColumn,
      hasAnyMarkupReadPermission,
      salesPriceCoverageColumn,
      priceSourceColumn,
      isStockedColumn,
      discontinuationColumn,
      i18n.language,
      onAddToAssortment,
      viewItemCalculusCard,
      viewItemProductCard,
    ]
  );

  const columns = columnConfigs.filter(({ show }) => show).map(({ column }) => column);

  return (
    <Box sx={{ flexGrow: 1, position: 'relative' }}>
      <Box sx={{ position: 'absolute', inset: 0 }}>
        <DataGridPremium<AssortmentPrices>
          getRowId={(row) => row.mgSupplierItemId}
          getRowClassName={getRowClassName}
          getCellClassName={() => clsx(dataGridCellNoFocusOutlineClass)}
          columns={columns}
          rows={items}
          rowHeight={70}
          loading={loading}
          disableRowSelectionOnClick
          disableColumnResize
          hideFooter
          initialState={{
            columns: {
              columnVisibilityModel: {
                ...getInitialHiddenDataGridColumnsFromLocalStorage(
                  DATA_GRID_ID.CALCULUS_ITEMS_TABLE,
                  getTogglableDataGridColumns(columns)
                ),
              },
            },
            pinnedColumns: { right: ['actions'] },
          }}
          onColumnVisibilityModelChange={(_, details) => {
            const toggableColumns = getTogglableDataGridColumns(columns);
            saveHiddenDataGridColumnsToLocalStorage(
              DATA_GRID_ID.CALCULUS_ITEMS_TABLE,
              toggableColumns,
              details.api.state.columns.columnVisibilityModel
            );
          }}
          slots={{
            columnHeaderSortIcon: (props) => <CustomSortableColumn sorting={props.direction} />,
            columnMenu: CustomColumnMenu,
            noRowsOverlay: () => (
              <NoTableRowsOverlay noRowsText={t('myAssortment.filters.noMatchOnSearch')} />
            ),
          }}
        />
      </Box>
    </Box>
  );
};
